import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ScrollView } from "react-native";
import { YStack } from "tamagui";
import { useDebounce } from "use-debounce";
import { z } from "zod";

import {
  useGetInsuranceCarriers,
  useGetInsurancePlansInfinite,
} from "@/api/generated";
import type { ZocdocInsuranceCarrier } from "@/api/generated/models";
import { useGetUserProfile } from "@/api/queries/userProfile";
import { Button, ComboBox, Input, Paragraph } from "@/components";
import { PhaseContainer } from "./PhaseContainer";

const PLANS_PAGE_SIZE = 25;

const formatNetworkType = (networkType: string) =>
  networkType.toLowerCase() === "other" ? "Other" : networkType.toUpperCase();

const insuranceSchema = z.object({
  carrierName: z
    .string()
    .min(1, "Please select an insurance carrier from the below"),
  planId: z.string().min(1, "Please select an insurance plan"),
});

type InsuranceFormData = z.infer<typeof insuranceSchema>;

type InsurancePhaseProps = {
  onPlanSelected: (planId: string | null, carrierName: string | null) => void;
};

export const InsurancePhase = ({ onPlanSelected }: InsurancePhaseProps) => {
  const [carrierSearch, setCarrierSearch] = useState("");
  const [debouncedCarrierSearch] = useDebounce(carrierSearch, 300);
  const [selectedCarrier, setSelectedCarrier] =
    useState<ZocdocInsuranceCarrier | null>(null);
  const [prefilledFromProfile, setPrefilledFromProfile] = useState(false);
  const [planSearch, setPlanSearch] = useState("");
  const [debouncedPlanSearch] = useDebounce(planSearch, 300);

  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<InsuranceFormData>({
    resolver: zodResolver(insuranceSchema),
    mode: "onBlur",
    defaultValues: { carrierName: "", planId: "" },
  });

  const { data: profile } = useGetUserProfile();

  useEffect(() => {
    if (!prefilledFromProfile && profile?.carrierName) {
      setCarrierSearch(profile.carrierName);
      setPrefilledFromProfile(true);
    }
  }, [profile?.carrierName, prefilledFromProfile]);

  const { data: carriers = [] } = useGetInsuranceCarriers(
    { search: debouncedCarrierSearch },
    {
      query: {
        enabled: debouncedCarrierSearch.length >= 2 && !selectedCarrier,
      },
    },
  );

  const {
    data: plansData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingPlans,
  } = useGetInsurancePlansInfinite(
    {
      carrier_name: selectedCarrier?.carrier_name ?? "",
      search: debouncedPlanSearch,
      page_size: PLANS_PAGE_SIZE,
    },
    {
      query: {
        enabled: !!selectedCarrier,
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
          const nextPage = lastPage.page + 1;
          const totalPages = Math.ceil(lastPage.total / lastPage.page_size);
          return nextPage <= totalPages ? nextPage : undefined;
        },
      },
    },
  );

  const plans = plansData?.pages.flatMap((page) => page.plans) ?? [];

  const resetPlan = () => {
    setPlanSearch("");
    setValue("planId", "");
  };

  const onSubmit = handleSubmit(({ planId }) =>
    onPlanSelected(planId, selectedCarrier?.carrier_name ?? null),
  );

  return (
    <PhaseContainer title="Find your insurance" currentStep={1}>
      <Input.Controlled
        label="Insurance carrier"
        placeholder="Search by carrier name..."
        value={carrierSearch}
        onChangeText={(text) => {
          setCarrierSearch(text);
          setSelectedCarrier(null);
          setValue("carrierName", "");
          clearErrors(["carrierName", "planId"]);
          resetPlan();
        }}
        errorText={errors.carrierName?.message}
      />
      {!selectedCarrier && carriers.length > 0 && (
        <YStack maxHeight={200}>
          <ScrollView>
            <YStack gap="$100">
              {carriers.map((carrier) => (
                <Button.Wrapper
                  key={carrier.carrier_name}
                  dataTrackingId="zocdoc-insurance-carrier"
                  onPress={() => {
                    setSelectedCarrier(carrier);
                    setCarrierSearch(carrier.carrier_name);
                    setValue("carrierName", carrier.carrier_name, {
                      shouldValidate: true,
                    });
                    resetPlan();
                  }}
                  backgroundColor="$background"
                  borderRadius="$100"
                  borderWidth="$strokeWidth"
                  borderColor="$strokeNeutral"
                  padding="$150"
                  alignItems="flex-start"
                >
                  <Paragraph size="$sm">{carrier.carrier_name}</Paragraph>
                </Button.Wrapper>
              ))}
            </YStack>
          </ScrollView>
        </YStack>
      )}
      {selectedCarrier && (
        <Controller
          control={control}
          name="planId"
          render={({ field: { onChange } }) => (
            <ComboBox
              label="Plan name"
              placeholder="Search by plan name..."
              dataTrackingId="zocdoc-insurance-plan"
              searchText={planSearch}
              onSearchTextChange={(text) => {
                setPlanSearch(text);
                onChange("");
                clearErrors("planId");
              }}
              items={plans.map((plan) => ({
                label: `${plan.plan_name} · ${formatNetworkType(plan.network_type)}`,
                value: plan.plan_id,
              }))}
              onSelect={(item) => {
                onChange(item.value);
                const plan = plans.find((p) => p.plan_id === item.value);
                setPlanSearch(plan?.plan_name ?? item.label);
              }}
              onEndReached={() => {
                if (hasNextPage) {
                  fetchNextPage();
                }
              }}
              isFetchingMore={isFetchingNextPage}
              isLoading={isLoadingPlans}
              emptyText="No plans match your search."
              errorText={errors.planId?.message}
            />
          )}
        />
      )}
      <Button mode="primary" alignSelf="flex-end" onPress={onSubmit}>
        Continue
      </Button>
    </PhaseContainer>
  );
};
